/**
 * One traffic source on the hub: its numbers for the period, how it compares
 * with the best source, and its sub-sources as small rows. The whole card
 * opens the source; the status pill and the child rows are their own targets.
 */
import { Link } from "react-router-dom";
import { CheckSquare, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar } from "@/components/ui";
import { Card, Delta, ShareBar, dotClass, firstName, num, pct, usd } from "@/features/command/ui";
import { SourceGlyph, StatusMenu, kindLabel } from "./shared";
import type { HubSource, SourceNumbers, SourceStatus } from "./types";

export type Metric = "revenue" | "signups" | "visits";
export const METRIC_LABEL: Record<Metric, string> = { revenue: "revenue", signups: "signups", visits: "visits" };

export function isQuiet(s: HubSource): boolean {
  const n = s.n;
  const any = n && (n.visits || n.signups || n.trials || n.paying || n.revenue || n.spend);
  return !any && !s.people;
}

const metricValue = (n: SourceNumbers | null, m: Metric) => Number(n?.[m] ?? 0);

export function SourceCard({
  source, kids, metric, best, total, days, from, onStatus, index,
}: {
  source: HubSource;
  kids: HubSource[];
  metric: Metric;
  best: number;
  total: number;
  days: number;
  from: string;
  onStatus: (s: HubSource, status: SourceStatus) => void;
  index: number;
}) {
  const n = source.n;
  const quiet = isQuiet(source) && kids.every(isQuiet);
  const value = metricValue(n, metric);
  const prevValue = metricValue(source.prev, metric);
  const href = `/traffic/${source.slug}${days !== 30 ? `?days=${days}` : ""}`;
  const fmt = (m: Metric, v: number) => (m === "revenue" ? usd(v) : num(v));
  const openTasks = source.open_tasks + kids.reduce((a, k) => a + k.open_tasks, 0);
  const people = source.people + kids.reduce((a, k) => a + k.people, 0);

  return (
    <div className="cc-rise h-full" style={{ animationDelay: `${Math.min(index, 12) * 30}ms` }}>
    <Card
      hover
      className={cn(
        "group relative flex h-full flex-col p-4 transition-opacity",
        quiet && "opacity-60 hover:opacity-100 focus-within:opacity-100",
      )}
    >
      <div className="flex items-start gap-3">
        <SourceGlyph icon={source.icon} kind={source.kind} color={source.color} />
        <div className="min-w-0 flex-1">
          <Link
            to={href}
            state={{ from }}
            className="block truncate text-[15px] font-semibold text-zinc-50 after:absolute after:inset-0 after:rounded-2xl after:content-[''] focus-visible:outline-none"
          >
            {source.name}
          </Link>
          <div className="mt-0.5 truncate text-xs text-zinc-500">{kindLabel(source.kind)}</div>
        </div>
        <StatusMenu status={source.status} onChange={(s) => onStatus(source, s)} className="relative z-10 shrink-0" />
      </div>

      {quiet ? (
        <div className="mt-3">
          <p className="line-clamp-2 text-xs leading-relaxed text-zinc-400">{source.description || "On the plan. Numbers show up once people arrive from here."}</p>
          <div className="mt-2 text-[11px] text-zinc-500">No numbers yet</div>
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <Num label="Signups" value={num(n?.signups)} strong={metric === "signups"} />
            <Num label="Paying" value={num(n?.paying)} />
            <Num label="Revenue" value={usd(n?.revenue, { compact: true })} strong={metric === "revenue"} />
          </div>

          <div className="mt-3">
            <ShareBar value={value} max={best} />
            <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px] text-zinc-500">
              <span className="truncate">
                {total > 0 && value > 0 ? `${pct(value / total)} of all ${METRIC_LABEL[metric]}` : `No ${METRIC_LABEL[metric]} this period`}
              </span>
              {value > 0 || prevValue > 0 ? (
                <span className="flex items-center gap-1" title={`Last period: ${fmt(metric, prevValue)}`}>
                  <Delta now={value} prev={prevValue} />
                </span>
              ) : null}
            </div>
          </div>
        </>
      )}

      {kids.length > 0 && (
        <div className="relative z-10 mt-3 space-y-0.5 rounded-xl border border-zinc-800/70 bg-zinc-900/40 p-1">
          {kids.map((k) => (
            <ChildRow key={k.id} child={k} days={days} from={from} onStatus={onStatus} />
          ))}
        </div>
      )}

      <div className="mt-auto flex items-center gap-3 pt-4 text-xs text-zinc-500">
        <span className="flex min-w-0 items-center gap-1.5">
          {source.owner_name ? <Avatar name={source.owner_name} size="xs" className="h-5 w-5 text-[9px]" /> : <span className="h-5 w-5 rounded-full border border-dashed border-zinc-700" />}
          <span className="truncate">{source.owner_name ? firstName(source.owner_name) : "No owner"}</span>
        </span>
        <span className="ml-auto flex shrink-0 items-center gap-3 tabular-nums">
          <span title="Visits this period">{num(n?.visits)} visits</span>
          <span className="flex items-center gap-1" title="People credited to this source (all time)"><Users className="h-3.5 w-3.5" />{num(people)}</span>
          <span className={cn("flex items-center gap-1", openTasks > 0 && "text-zinc-300")} title="Open tasks"><CheckSquare className="h-3.5 w-3.5" />{num(openTasks)}</span>
        </span>
      </div>
    </Card>
    </div>
  );
}

function Num({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="min-w-0">
      <div className={cn("truncate text-xl font-semibold tabular-nums tracking-tight", strong ? "text-zinc-50" : "text-zinc-200")}>{value}</div>
      <div className="text-[11px] text-zinc-500">{label}</div>
    </div>
  );
}

function ChildRow({ child, days, from, onStatus }: { child: HubSource; days: number; from: string; onStatus: (s: HubSource, status: SourceStatus) => void }) {
  const n = child.n;
  const quiet = isQuiet(child);
  return (
    <div className={cn("flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-zinc-800/50", quiet && "opacity-70")}>
      <Link to={`/traffic/${child.slug}${days !== 30 ? `?days=${days}` : ""}`} state={{ from }} className="flex min-w-0 flex-1 items-center gap-2">
        <span className={cn("h-2 w-2 shrink-0 rounded-full", dotClass(child.color))} />
        <span className="truncate text-xs font-medium text-zinc-200">{child.name}</span>
        <span className="ml-auto shrink-0 text-[11px] tabular-nums text-zinc-500">
          {num(n?.signups)} signups{n?.revenue ? ` · ${usd(n.revenue, { compact: true })}` : ""}
        </span>
      </Link>
      <StatusMenu status={child.status} onChange={(s) => onStatus(child, s)} className="shrink-0 scale-90" />
    </div>
  );
}
